import { useMemo, useRef, useState } from "react";
import { format } from "date-fns";

import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { getRouter } from "@/services/router/router";

const PRINT_DATE_FORMAT = "dd/MM/yyyy";
const PRINT_TIME_FORMAT = "HH:mm";
const SKELETON_ROWS = ["alpha", "bravo", "charlie", "delta"];

const formatCurrency = (value: number) =>
	new Intl.NumberFormat("pt-PT", {
		style: "currency",
		currency: "EUR",
		maximumFractionDigits: 2,
	}).format(value);

const printContents = (html: string) => {
	const printWindow = window.open("", "PRINT", "height=400,width=600");
	if (!printWindow) {
		return;
	}

	printWindow.document.write(`<html><head><title>${document.title}</title>`);
	printWindow.document.write("</head><body>");
	printWindow.document.write(`<h1>${document.title}</h1>`);
	printWindow.document.write(html);
	printWindow.document.write("</body></html>");
	printWindow.document.close();
	printWindow.focus();
	printWindow.print();
	printWindow.close();
};

type MaterialSummary = {
	brand?: string | null;
	model?: string | null;
	description?: string | null;
	price?: number | null;
};

type ClientSummary = {
	name?: string | null;
	phone?: string | null;
};

type RepairSummary = {
	_id?: string | null;
};

type ClientRequestEntry = {
	id?: string | number | null;
	requestNumber?: string | number | null;
	createdAt?: string | Date | null;
	material: MaterialSummary;
	quantity?: number | null;
	client: ClientSummary;
	storeBox?: string | null;
	quotation?: number | null;
	price?: number | null;
	observation?: string | null;
	state?: string | null;
	repair?: RepairSummary | null;
};

type ClientRequestsListProps = {
	data: ClientRequestEntry[];
	loading?: boolean;
	isAttendance: boolean;
	canEditRequests: boolean;
	onProcessRequest: (entry: ClientRequestEntry) => void;
	onRemoveRequest: (entry: ClientRequestEntry) => void;
	onDeliverMaterial: (entry: ClientRequestEntry) => void;
	onPutMaterialOnStock: (entry: ClientRequestEntry) => void;
};

const buildMaterialLabel = (material: MaterialSummary) => {
	const brand = material.brand?.trim() ?? "";
	const model = material.model?.trim() ?? "";
	const description = material.description?.trim() ?? "";
	const parts = [brand, model, description].filter((value) => value.length > 0);
	return parts.join(" ") || "-";
};

const formatDate = (
	value?: string | Date | null,
	dateFormat = PRINT_DATE_FORMAT,
) => {
	if (!value) {
		return "-";
	}

	const date = typeof value === "string" ? new Date(value) : value;
	if (Number.isNaN(date.getTime())) {
		return "-";
	}

	return format(date, dateFormat);
};

const sortRequests = (data: ClientRequestEntry[]) =>
	[...data].sort((a, b) => {
		const aValue = Number(a.requestNumber ?? 0);
		const bValue = Number(b.requestNumber ?? 0);
		return bValue - aValue;
	});

export function ClientRequestsList({
	data,
	loading = false,
	isAttendance,
	canEditRequests,
	onProcessRequest,
	onRemoveRequest,
	onDeliverMaterial,
	onPutMaterialOnStock,
}: ClientRequestsListProps) {
	const [printDialogOpen, setPrintDialogOpen] = useState(false);
	const [clientDialogOpen, setClientDialogOpen] = useState(false);
	const [selectedClient, setSelectedClient] = useState<ClientSummary | null>(
		null,
	);
	const printRef = useRef<HTMLDivElement | null>(null);

	const sortedData = useMemo(() => sortRequests(data), [data]);
	const canShowActions = isAttendance || canEditRequests;

	const handlePrint = () => {
		setPrintDialogOpen(false);
		if (printRef.current) {
			printContents(printRef.current.innerHTML);
		}
	};

	const handleShowClient = (client: ClientSummary) => {
		setSelectedClient(client);
		setClientDialogOpen(true);
	};

	const handleShowRepair = async (repair?: RepairSummary | null) => {
		if (!repair?._id) {
			return;
		}

		const router = await getRouter();
		router.push({ name: "repair.single", params: { id: repair._id } });
	};

	const renderPrintTable = () => (
		<div ref={printRef}>
			<Table>
				<TableHeader>
					<TableRow className="bg-muted/50">
						<TableHead className="w-[90px] text-center">#Pedido</TableHead>
						<TableHead className="w-[180px]">Cliente</TableHead>
						<TableHead>Material</TableHead>
						<TableHead className="w-[80px]">Qnt.</TableHead>
						<TableHead className="w-[110px] text-center">
							Preço unidade
						</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{sortedData.map((entry) => (
						<TableRow key={entry.id ?? entry.requestNumber ?? "print-row"}>
							<TableCell className="text-center">
								{entry.requestNumber ?? "-"}
							</TableCell>
							<TableCell>{entry.client?.name ?? "-"}</TableCell>
							<TableCell>{buildMaterialLabel(entry.material)}</TableCell>
							<TableCell>{entry.quantity ?? "-"}</TableCell>
							<TableCell className="text-center">
								{formatCurrency(entry.price ?? entry.material.price ?? 0)}
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);

	const renderSkeleton = () => (
		<TableBody>
			{SKELETON_ROWS.map((rowKey) => (
				<TableRow
					key={`client-requests-skeleton-${rowKey}`}
					className="animate-pulse"
				>
					<TableCell>
						<div className="h-4 w-16 rounded bg-muted/70" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-24 rounded bg-muted/60" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-24 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-48 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-10 rounded bg-muted/40" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-32 rounded bg-muted/40" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-12 rounded bg-muted/40" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-20 rounded bg-muted/30" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-24 rounded bg-muted/30" />
					</TableCell>
					{canShowActions ? (
						<TableCell className="text-right">
							<div className="ml-auto h-8 w-32 rounded bg-muted/30" />
						</TableCell>
					) : null}
				</TableRow>
			))}
		</TableBody>
	);

	return (
		<section className="client-requests-list">
			<Button
				variant="secondary"
				size="sm"
				className="tp-MaterialRequestList__newBtn"
				onClick={() => setPrintDialogOpen(true)}
				disabled={loading}
			>
				Imprimir
			</Button>

			<Dialog open={printDialogOpen} onOpenChange={setPrintDialogOpen}>
				<DialogContent className="max-h-[80vh] w-[95vw] max-w-3xl overflow-y-auto">
					<DialogHeader>
						<DialogTitle>Pedido</DialogTitle>
					</DialogHeader>
					{renderPrintTable()}
					<DialogFooter>
						<Button variant="outline" onClick={() => setPrintDialogOpen(false)}>
							Cancelar
						</Button>
						<Button onClick={handlePrint}>Imprimir</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog open={clientDialogOpen} onOpenChange={setClientDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Informação do cliente</DialogTitle>
					</DialogHeader>
					<div className="space-y-2 text-sm">
						<div>
							<p className="text-muted-foreground">Nome</p>
							<p>{selectedClient?.name ?? "-"}</p>
						</div>
						<div>
							<p className="text-muted-foreground">Telefone</p>
							<p>{selectedClient?.phone ?? "-"}</p>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setClientDialogOpen(false)}
						>
							Fechar
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<div className="material-requests-list material-requests-list--client">
				<div className="overflow-hidden rounded-lg border border-border/60 bg-card/70">
					<Table>
						<TableHeader>
							<TableRow className="bg-muted/50">
								<TableHead className="w-[90px] text-center">#Pedido</TableHead>
								<TableHead>Data</TableHead>
								<TableHead>Hora</TableHead>
								<TableHead>Descrição</TableHead>
								<TableHead className="w-[80px] text-center">Qtn.</TableHead>
								<TableHead>Cliente</TableHead>
								<TableHead className="w-[80px] text-center">Caixa</TableHead>
								<TableHead className="w-[140px] text-center">
									Preço unidade
								</TableHead>
								<TableHead>Observação</TableHead>
								{canShowActions ? (
									<TableHead className="w-[240px] text-right" />
								) : null}
							</TableRow>
						</TableHeader>
						{loading ? (
							renderSkeleton()
						) : (
							<TableBody>
								{sortedData.map((entry) => {
									const rowKey =
										entry.id ??
										entry.requestNumber ??
										`${entry.material.description}`;
									const showActions =
										entry.state !== "waiting_delivery" && !isAttendance;

									return (
										<TableRow key={rowKey}>
											<TableCell className="text-center">
												{entry.requestNumber ?? "-"}
											</TableCell>
											<TableCell>
												{formatDate(entry.createdAt, PRINT_DATE_FORMAT)}
											</TableCell>
											<TableCell>
												{formatDate(entry.createdAt, PRINT_TIME_FORMAT)}
											</TableCell>
											<TableCell>
												{buildMaterialLabel(entry.material)}
											</TableCell>
											<TableCell className="text-center">
												{entry.quantity ?? "-"}
											</TableCell>
											<TableCell>
												<button
													type="button"
													className="text-primary underline-offset-2 hover:underline"
													onClick={() => handleShowClient(entry.client)}
												>
													{entry.client?.name ?? "-"}
												</button>
											</TableCell>
											<TableCell className="text-center">
												{entry.storeBox ?? "-"}
											</TableCell>
											<TableCell className="text-center">
												{formatCurrency(
													entry.quotation ?? entry.material.price ?? 0,
												)}
											</TableCell>
											<TableCell>{entry.observation ?? "-"}</TableCell>
											{canShowActions ? (
												<TableCell className="text-right">
													<div className="flex justify-end gap-2">
														{entry.repair ? (
															<Button
																variant="secondary"
																size="sm"
																onClick={() => handleShowRepair(entry.repair)}
															>
																Reparação
															</Button>
														) : null}
														{showActions ? (
															<>
																<Button
																	size="sm"
																	onClick={() => onProcessRequest(entry)}
																>
																	Comprar
																</Button>
																<Button
																	variant="destructive"
																	size="sm"
																	onClick={() => onRemoveRequest(entry)}
																>
																	Cancelar
																</Button>
															</>
														) : null}
														{!showActions ? (
															<>
																{(isAttendance && !entry.repair) ||
																canEditRequests ? (
																	<Button
																		size="sm"
																		onClick={() => onDeliverMaterial(entry)}
																	>
																		Entregar
																	</Button>
																) : null}
																{!isAttendance ? (
																	<Button
																		variant="secondary"
																		size="sm"
																		onClick={() => onPutMaterialOnStock(entry)}
																	>
																		Colocar no stock
																	</Button>
																) : null}
															</>
														) : null}
													</div>
												</TableCell>
											) : null}
										</TableRow>
									);
								})}
							</TableBody>
						)}
					</Table>
				</div>
			</div>
		</section>
	);
}
